/* eslint-disable no-underscore-dangle */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Button,
  Form,
  Badge,
  Collapse,
  Spinner,
  InputGroup,
  Table,
} from 'react-bootstrap';
import _ from 'lodash';
import { get } from '../../../Services/API';
import { formatBookingDateOnly, formatBookingTimeOnly } from '../../../Utils/timezone';

/**
 * Link Bookings Panel Component
 * Allows users to search and link related bookings (e.g., outbound + return transfers)
 */
function LinkBookingsPanel({ linkedBookingIds = [], onLinkedBookingsChange }) {
  const [isOpen, setIsOpen] = useState(linkedBookingIds.length > 0);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [linkedBookings, setLinkedBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingLinked, setLoadingLinked] = useState(false);

  // Fetch linked booking details on mount or when IDs change
  // Only fetch if we don't already have the booking data in state
  useEffect(() => {
    const fetchLinkedBookings = async () => {
      if (!linkedBookingIds || linkedBookingIds.length === 0) {
        setLinkedBookings([]);
        return;
      }

      // Check which IDs we don't already have in state
      const existingIds = linkedBookings.map((b) => b._id);
      const missingIds = linkedBookingIds.filter((id) => !existingIds.includes(id));

      // If we already have all the bookings, no need to fetch
      if (missingIds.length === 0) {
        // But remove any bookings that are no longer in linkedBookingIds
        setLinkedBookings((prev) => prev.filter((b) => linkedBookingIds.includes(b._id)));
        return;
      }

      setLoadingLinked(true);
      try {
        // Fetch all bookings at once using the filters that backend supports
        const response = await get('/bookings', {
          filters: { _id: { $in: linkedBookingIds } },
        });
        const documents = response.data?.data?.documents || [];
        setLinkedBookings(documents);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error fetching linked bookings:', err);
      } finally {
        setLoadingLinked(false);
      }
    };

    fetchLinkedBookings();
  }, [linkedBookingIds]);

  // Debounced search function
  const searchBookings = useCallback(
    _.debounce(async (term) => {
      if (!term || term.length < 2) {
        setSearchResults([]);
        return;
      }

      setLoading(true);
      try {
        // Use quick_search which searches booking_reference, email, phone, etc.
        const response = await get('/bookings', {
          filters: {
            quick_search: term,
          },
          limit: 10,
        });
        const documents = response.data?.data?.documents || [];
        // Filter out already linked bookings
        const filtered = documents.filter(
          (doc) => !linkedBookingIds.includes(doc._id),
        );
        setSearchResults(filtered);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error searching bookings:', err);
      } finally {
        setLoading(false);
      }
    }, 300),
    [linkedBookingIds],
  );

  const handleSearchChange = (e) => {
    const { value } = e.target;
    setSearchTerm(value);
    searchBookings(value);
  };

  const handleAddBooking = (booking) => {
    const newIds = [...linkedBookingIds, booking._id];
    onLinkedBookingsChange(newIds);
    // Immediately add booking to linkedBookings for instant UI feedback
    setLinkedBookings((prev) => [...prev, booking]);
    setSearchTerm('');
    setSearchResults([]);
  };

  const handleRemoveBooking = (bookingId) => {
    const newIds = linkedBookingIds.filter((id) => id !== bookingId);
    onLinkedBookingsChange(newIds);
    // Immediately remove booking from linkedBookings for instant UI feedback
    setLinkedBookings((prev) => prev.filter((b) => b._id !== bookingId));
  };

  const getStatusBadge = (status) => {
    const variants = {
      Confirmed: 'success',
      Completed: 'primary',
      Cancelled: 'danger',
      Request: 'warning',
      'Driver No-Show': 'dark',
      'Passenger No-Show': 'secondary',
    };
    return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
  };

  const renderBookingRow = (booking, isSearchResult = false) => (
    <tr key={booking._id}>
      <td>
        <div className="fw-bold">{booking.full_name || '-'}</div>
        <small className="text-muted">{booking.booking_reference}</small>
      </td>
      <td>
        <div className="fw-bold">
          {formatBookingTimeOnly(booking)}
        </div>
        <small className="text-muted">
          {formatBookingDateOnly(booking)}
        </small>
      </td>
      <td>
        <small className="text-truncate d-block" style={{ maxWidth: '150px' }}>
          {booking.destination_from?.address || '-'}
        </small>
      </td>
      <td>
        <small className="text-truncate d-block" style={{ maxWidth: '150px' }}>
          {booking.destination_to?.address || '-'}
        </small>
      </td>
      <td>{getStatusBadge(booking.status)}</td>
      <td>
        {isSearchResult ? (
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => handleAddBooking(booking)}
          >
            <i className="fa fa-plus" />
          </Button>
        ) : (
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => handleRemoveBooking(booking._id)}
          >
            <i className="fa fa-times" />
          </Button>
        )}
      </td>
    </tr>
  );

  return (
    <Card className="mb-3">
      <Card.Header
        onClick={() => setIsOpen(!isOpen)}
        style={{ cursor: 'pointer' }}
        className="d-flex justify-content-between align-items-center"
      >
        <div className="d-flex align-items-center gap-2">
          <i className="fa fa-link" />
          <span>Linked Bookings</span>
          {linkedBookingIds.length > 0 && (
            <Badge bg="primary" pill>
              {linkedBookingIds.length}
            </Badge>
          )}
        </div>
        <Badge bg="secondary" pill>
          {isOpen ? '▲' : '▼'}
        </Badge>
      </Card.Header>
      <Collapse in={isOpen}>
        <Card.Body>
          {/* Search input */}
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">
              <i className="fa fa-search me-2" />
              Search bookings to link
            </Form.Label>
            <InputGroup>
              <Form.Control
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search by reference, email, phone..."
              />
              {loading && (
                <InputGroup.Text>
                  <Spinner animation="border" size="sm" />
                </InputGroup.Text>
              )}
            </InputGroup>
          </Form.Group>

          {/* Search results table */}
          {searchResults.length > 0 && (
            <div className="mb-3">
              <Form.Label className="fw-semibold text-primary">
                <i className="fa fa-search me-2" />
                Search Results
              </Form.Label>
              <Table responsive striped bordered hover size="sm">
                <thead className="table-light">
                  <tr>
                    <th>Passenger</th>
                    <th style={{ width: '80px' }}>Time</th>
                    <th>From</th>
                    <th>To</th>
                    <th style={{ width: '80px' }}>Status</th>
                    <th style={{ width: '50px' }} />
                  </tr>
                </thead>
                <tbody>
                  {searchResults.map((booking) => renderBookingRow(booking, true))}
                </tbody>
              </Table>
            </div>
          )}

          {/* Linked bookings table */}
          {(() => {
            if (loadingLinked) {
              return (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" />
                  <span className="ms-2">Loading linked bookings...</span>
                </div>
              );
            }
            if (linkedBookings.length > 0) {
              return (
                <>
                  <Form.Label className="fw-semibold">
                    <i className="fa fa-list me-2" />
                    Currently Linked
                  </Form.Label>
                  <Table responsive striped bordered hover size="sm">
                    <thead className="table-light">
                      <tr>
                        <th>Passenger</th>
                        <th style={{ width: '80px' }}>Time</th>
                        <th>From</th>
                        <th>To</th>
                        <th style={{ width: '80px' }}>Status</th>
                        <th style={{ width: '50px' }} />
                      </tr>
                    </thead>
                    <tbody>
                      {linkedBookings.map((booking) => renderBookingRow(booking, false))}
                    </tbody>
                  </Table>
                </>
              );
            }
            return (
              <div className="text-muted text-center py-2">
                No linked bookings. Search above to link related bookings.
              </div>
            );
          })()}
        </Card.Body>
      </Collapse>
    </Card>
  );
}

export default LinkBookingsPanel;
